import React, { useEffect, useState } from 'react';
import { Search, ShoppingCart, UserPlus, Headphones, Sparkles, Play, Pause, Trash2, ChevronRight, Loader2, X, AlertCircle, Check, ArrowLeft } from 'lucide-react';
import Header from '../layout/Header';
import { API_URL } from '../../lib/apiConfig';
import { useAuth } from '../../contexts/AuthContext';
import FlowEditor from '../FlowEditor';

const safeToken = () => { try { return localStorage.getItem('autopostr_token'); } catch { return null; } };

// Defensive response reader — tolerates HTML/empty bodies (ingress 502/503).
const readResponse = async (r) => {
  const ct = r.headers.get('content-type') || '';
  const text = await r.text();
  if (!ct.includes('json')) {
    return { parseError: true, text: text.slice(0, 200) };
  }
  try { return JSON.parse(text); } catch { return { parseError: true, text: text.slice(0, 200) }; }
};

// Build a human-readable error from an error response body.
const formatError = (body, fallback = 'Request failed') => {
  if (!body) return fallback;
  if (body.parseError) return `Server returned a non-JSON response (${body.text ? body.text.slice(0, 80) + '…' : 'empty'}). Try again in a moment.`;
  const d = body.detail;
  if (typeof d === 'string') return d;
  if (d && typeof d === 'object') {
    const msg = d.message || d.code || fallback;
    const reasons = d.reasons || [];
    const steps = d.steps && d.steps.length ? ` (steps: ${d.steps.join(', ')})` : '';
    return reasons.length > 1 ? `${msg}${steps}\n• ${reasons.join('\n• ')}` : `${msg}${steps}`;
  }
  return body.message || fallback;
};

const CATEGORIES = [
  { k: 'all', label: 'all', icon: null },
  { k: 'engagement', label: 'engagement', icon: <Sparkles size={14} /> },
  { k: 'sales', label: 'sales', icon: <ShoppingCart size={14} /> },
  { k: 'support', label: 'support', icon: <Headphones size={14} /> },
  { k: 'onboarding', label: 'onboarding', icon: <UserPlus size={14} /> },
];

const categoryIcon = (c) => {
  switch ((c || '').toLowerCase()) {
    case 'sales': return <ShoppingCart size={22} className="text-emerald-500" />;
    case 'onboarding': return <UserPlus size={22} className="text-orange-500" />;
    case 'support': return <Headphones size={22} className="text-sky-500" />;
    case 'engagement':
    default: return <Sparkles size={22} className="text-purple-500" />;
  }
};

export default function DMAutomationPage() {
  const { isAuthenticated, setShowAuthModal } = useAuth();
  const [tab, setTab] = useState('flows'); // default to flows (My Flows dashboard)
  const [hasInitialised, setHasInitialised] = useState(false);
  const [cat, setCat] = useState('all');
  const [q, setQ] = useState('');
  const [templates, setTemplates] = useState([]);
  const [flows, setFlows] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [chat33Channels, setChat33Channels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [using, setUsing] = useState(null);
  const [toast, setToast] = useState(null);
  const [confirm, setConfirm] = useState(null); // template to confirm
  const [editingFlow, setEditingFlow] = useState(null); // flow_id being edited
  const [activating, setActivating] = useState(null); // { flow, selected: [], loading }

  useEffect(() => {
    if (!isAuthenticated) return;
    loadTemplates();
    loadFlows(true);
    loadAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, cat, q]);

  const loadAccounts = async () => {
    try {
      const r = await fetch(`${API_URL}/api/social/accounts`, { headers: { Authorization: `Bearer ${safeToken()}` } });
      const d = await readResponse(r);
      if (r.ok) setAccounts(d.accounts || []);
    } catch {}
    try {
      const r2 = await fetch(`${API_URL}/api/dm/channels`, { headers: { Authorization: `Bearer ${safeToken()}` } });
      const d2 = await readResponse(r2);
      if (r2.ok) setChat33Channels(d2.channels || []);
    } catch {}
  };

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (cat !== 'all') params.set('category', cat);
      if (q) params.set('q', q);
      const r = await fetch(`${API_URL}/api/dm/templates?${params}`, { headers: { Authorization: `Bearer ${safeToken()}` } });
      const d = await readResponse(r);
      if (!r.ok) throw new Error(formatError(d, 'Failed to load templates'));
      setTemplates(d.items || []);
    } catch (e) {
      setToast({ type: 'error', text: e.message });
    } finally {
      setLoading(false);
    }
  };

  const loadFlows = async (fromInit = false) => {
    try {
      const r = await fetch(`${API_URL}/api/dm/flows`, { headers: { Authorization: `Bearer ${safeToken()}` } });
      const d = await readResponse(r);
      if (!r.ok) return;
      const items = d.items || [];
      setFlows(items);
      // On first load, if no flows exist, switch to templates so new users see the catalogue.
      if (fromInit && !hasInitialised) {
        if (items.length === 0) setTab('templates');
        setHasInitialised(true);
      }
    } catch {}
  };

  // Auto-refresh Chat33 dashboard channels while the activate modal is open so a newly-
  // connected IG account appears without the user having to close/reopen the dialog.
  useEffect(() => {
    if (!activating) return;
    const loadAll = async () => {
      try {
        const [r, r2] = await Promise.all([
          fetch(`${API_URL}/api/dm/channels`, { headers: { Authorization: `Bearer ${safeToken()}` } }),
          fetch(`${API_URL}/api/social/accounts`, { headers: { Authorization: `Bearer ${safeToken()}` } }),
        ]);
        const d = await readResponse(r);
        const d2 = await readResponse(r2);
        if (r.ok) setChat33Channels(prev => {
          const next = d.channels || [];
          const prevIds = new Set(prev.map(c => c.id));
          const newly = next.filter(c => !prevIds.has(c.id));
          if (newly.length && prev.length > 0) {
            setActivating(a => a ? { ...a, selected: Array.from(new Set([...a.selected, newly[0].id])) } : a);
            setToast({ type: 'success', text: `New Chat33 channel detected: ${newly[0].username || newly[0].id}. Selected for you.` });
          }
          return next;
        });
        if (r2.ok) setAccounts(prev => {
          const next = d2.accounts || [];
          const prevIds = new Set(prev.map(c => c.id));
          const newly = next.filter(c => !prevIds.has(c.id));
          if (newly.length && prev.length > 0) {
            setActivating(a => a ? { ...a, selected: Array.from(new Set([...a.selected, newly[0].id])) } : a);
            setToast({ type: 'success', text: `New Instagram account connected: ${newly[0].username || newly[0].id}. Selected for you.` });
          }
          return next;
        });
      } catch {}
    };
    const t = setInterval(loadAll, 5000);
    return () => clearInterval(t);
  }, [activating?.flow?.flow_id]);

  // Open the Chat33-powered OAuth connect flow via AutoPostr's existing /api/social/connect.
  // The URL lives under YOUR Chat33 workspace — the end user completes IG/FB consent on
  // Meta's native screen and the new account appears under their AutoPostr profile.
  const openConnect = async (platform) => {
    try {
      const r = await fetch(`${API_URL}/api/social/connect/${platform}`, { headers: { Authorization: `Bearer ${safeToken()}` } });
      const d = await readResponse(r);
      if (!r.ok || !d.authUrl) throw new Error(formatError(d, 'Could not start connect flow'));
      window.open(d.authUrl, '_blank', 'noopener,noreferrer');
      setToast({ type: 'success', text: `Opening ${platform} authorization… We'll pick up the new account automatically.` });
    } catch (e) {
      setToast({ type: 'error', text: e.message });
    }
  };

  const handleUseTemplate = async (tpl) => {
    setUsing(tpl.id);
    setToast(null);
    try {
      const r = await fetch(`${API_URL}/api/dm/templates/${tpl.id}/use`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${safeToken()}`, 'Content-Type': 'application/json' },
      });
      const d = await readResponse(r);
      if (!r.ok) throw new Error(formatError(d, 'Failed to use template'));
      setToast({ type: 'success', text: `${tpl.name} added as a draft flow. Review and activate it below.` });
      await loadFlows();
      setTab('flows');
    } catch (e) {
      setToast({ type: 'error', text: e.message });
    } finally {
      setUsing(null);
      setConfirm(null);
    }
  };

  const patchFlowStatus = async (flow, nextStatus) => {
    // Activating opens the account-picker instead of flipping directly.
    if (nextStatus === 'ACTIVE') {
      setActivating({ flow, selected: [], loading: true, instagramOnly: false, hasCommentTrigger: false, full: null });
      try {
        const r = await fetch(`${API_URL}/api/dm/flows/${flow.flow_id}`, { headers: { Authorization: `Bearer ${safeToken()}` } });
        const d = await readResponse(r);
        if (!r.ok) throw new Error(formatError(d, 'Failed to load flow'));
        const igOnly = flowIsInstagramOnly(d);
        const trig = (d.nodes || []).find(n => n.type === 'trigger');
        const hasCommentTrigger = (trig?.data?.triggerType || '').toUpperCase() === 'COMMENT_KEYWORD';
        const existing = trig?.data?.channelId ? [trig.data.channelId] : (trig?.data?.channelIds || []);
        setActivating({ flow, selected: existing, loading: false, instagramOnly: igOnly, hasCommentTrigger, full: d });
      } catch (e) {
        setActivating({ flow, selected: [], loading: false, instagramOnly: false, hasCommentTrigger: false, full: null, error: e.message });
      }
      return;
    }
    try {
      const r = await fetch(`${API_URL}/api/dm/flows/${flow.flow_id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${safeToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: nextStatus }),
      });
      const d = await readResponse(r);
      if (!r.ok) throw new Error(formatError(d, 'Status change failed'));
      setToast({ type: 'success', text: `Flow is now ${nextStatus}.` });
      await loadFlows();
    } catch (e) {
      setToast({ type: 'error', text: e.message });
    }
  };

  // Flow needs the Instagram-only path when it has these nodes (publicly reply to
  // comments and the follow-gate step both require Meta's IG Graph API).
  const flowIsInstagramOnly = (f) => (f?.nodes || []).some(n => ['replyToComment', 'followCheck'].includes(n?.type));

  const confirmActivate = async () => {
    if (!activating) return;
    setActivating({ ...activating, loading: true });
    try {
      const r = await fetch(`${API_URL}/api/dm/flows/${activating.flow.flow_id}/activate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${safeToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ account_ids: activating.selected }),
      });
      const d = await readResponse(r);
      if (!r.ok) throw new Error(formatError(d, 'Activation failed'));
      if (d.warning) {
        // Chat33 silently refused to bind the channel — show the real reason and
        // keep the modal open so the user sees it in context.
        setActivating(a => a ? { ...a, loading: false, bindingWarning: d.warning } : a);
        await loadFlows();
        return;
      }
      setToast({
        type: 'success',
        text: `${activating.flow.name} is live on ${activating.selected.length} account(s).`,
      });
      setActivating(null);
      await loadFlows();
    } catch (e) {
      setActivating(a => a ? { ...a, loading: false, error: e.message } : a);
    }
  };

  const deleteFlow = async (flow) => {
    if (!window.confirm(`Delete "${flow.name}"? This can't be undone.`)) return;
    try {
      const r = await fetch(`${API_URL}/api/dm/flows/${flow.flow_id}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${safeToken()}` }
      });
      if (!r.ok) throw new Error('Delete failed');
      setToast({ type: 'success', text: `${flow.name} deleted.` });
      await loadFlows();
    } catch (e) {
      setToast({ type: 'error', text: e.message });
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white">
        <Header />
        <div className="max-w-xl mx-auto px-5 py-24 text-center" data-testid="dm-auth-prompt">
          <h1 className="text-3xl font-bold mb-3">DM Automation</h1>
          <p className="text-white/60 mb-6">Sign in to build keyword flows that reply to your DMs, comments and story replies automatically.</p>
          <button onClick={() => setShowAuthModal(true)} className="px-6 py-3 rounded-full bg-emerald-500 text-white font-semibold">Sign in</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="dm-page">
      <Header />

      {/* Confirm modal */}
      {confirm && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="dm-confirm-modal">
          <div className="bg-[#111] border border-white/10 rounded-2xl max-w-md w-full p-6">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">{categoryIcon(confirm.category)}<h3 className="font-semibold text-lg">{confirm.name}</h3></div>
              <button onClick={() => setConfirm(null)} className="text-white/60 hover:text-white"><X size={18} /></button>
            </div>
            <p className="text-white/60 text-sm mb-5 leading-relaxed">{confirm.description}</p>
            <p className="text-white/40 text-xs mb-5">A draft flow will be created in your AutoPostr workspace. Review and activate it below before it starts replying.</p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setConfirm(null)} className="px-4 py-2 rounded-lg bg-white/5 text-white/70 text-sm">Cancel</button>
              <button data-testid="dm-confirm-use" onClick={() => handleUseTemplate(confirm)} disabled={using === confirm.id}
                className="px-4 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium disabled:opacity-50 flex items-center gap-2">
                {using === confirm.id ? <Loader2 size={14} className="animate-spin" /> : null} Use template
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-5 pt-28 sm:pt-32 pb-12">
        <a href="/dashboard" data-testid="dm-back-to-dashboard"
          className="inline-flex items-center gap-1.5 text-white/50 hover:text-white text-xs mb-4 transition-colors">
          <ArrowLeft size={14} /> Back to Dashboard
        </a>
        <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold">DM Automation</h1>
            <p className="text-white/50 text-sm mt-1">Keyword, story-reply and comment-to-DM flows — part of AutoPostr automations.</p>
          </div>
          {/* Tabs */}
          <div className="flex gap-1 p-1 rounded-full bg-white/5 border border-white/10" data-testid="dm-tabs">
            <button data-testid="dm-tab-templates" onClick={() => setTab('templates')}
              className={`px-4 py-1.5 rounded-full text-sm ${tab === 'templates' ? 'bg-orange-500 text-white' : 'text-white/60 hover:text-white'}`}>Templates</button>
            <button data-testid="dm-tab-flows" onClick={() => setTab('flows')}
              className={`px-4 py-1.5 rounded-full text-sm ${tab === 'flows' ? 'bg-orange-500 text-white' : 'text-white/60 hover:text-white'}`}>My Flows {flows.length ? <span className="ml-1 text-xs opacity-70">{flows.length}</span> : null}</button>
          </div>
        </div>

        {toast && (
          <div data-testid="dm-toast" className={`mb-6 p-3 rounded-xl flex items-start gap-2 text-sm whitespace-pre-line ${toast.type === 'success' ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-400' : 'bg-red-500/10 border border-red-500/30 text-red-400'}`}>
            {toast.type === 'success' ? <Check size={16} className="mt-0.5 flex-shrink-0" /> : <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />}
            <span className="flex-1">{toast.text}</span>
            <button onClick={() => setToast(null)} className="text-current/60 hover:text-current"><X size={14} /></button>
          </div>
        )}

        {tab === 'templates' ? (
          <>
            {/* Search + categories */}
            <div className="flex items-center gap-3 mb-6 flex-wrap" data-testid="dm-template-filters">
              <div className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/5 border border-white/10 flex-1 min-w-[260px] max-w-xl">
                <Search size={16} className="text-white/40" />
                <input data-testid="dm-template-search" value={q} onChange={e => setQ(e.target.value)} placeholder="search templates..."
                  className="flex-1 bg-transparent text-white text-sm placeholder-white/40 outline-none" />
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {CATEGORIES.map(c => (
                  <button key={c.k} data-testid={`dm-cat-${c.k}`} onClick={() => setCat(c.k)}
                    className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-medium transition-colors ${cat === c.k ? 'bg-orange-500 text-white' : 'bg-white/5 text-white/70 border border-white/10 hover:bg-white/10'}`}>
                    {c.icon}{c.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Template grid */}
            {loading ? (
              <div className="flex items-center justify-center py-16 text-white/50"><Loader2 className="animate-spin mr-2" size={18} /> Loading templates…</div>
            ) : templates.length === 0 ? (
              <div className="text-center py-16 text-white/40 text-sm">No templates match your filters.</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="dm-template-grid">
                {templates.map(t => (
                  <div key={t.id} data-testid={`dm-template-${t.id}`}
                    className="rounded-2xl bg-[#111] border border-white/10 hover:border-orange-500/40 transition-all p-5 flex flex-col">
                    <div className="flex items-start justify-between mb-4">
                      {categoryIcon(t.category)}
                      <div className="flex gap-1.5">
                        <span className="text-xs px-2.5 py-1 rounded-full bg-white/5 text-white/70 border border-white/10 lowercase">{t.category}</span>
                        {t.official && <span className="text-xs px-2.5 py-1 rounded-full bg-white/10 text-white/60 border border-white/10">official</span>}
                      </div>
                    </div>
                    <h3 className="font-semibold text-lg mb-2 leading-snug">{t.name}</h3>
                    <p className="text-white/55 text-sm leading-relaxed flex-1 mb-5">{t.description}</p>
                    <div className="border-t border-white/5 pt-4 flex items-center justify-between">
                      <p className="text-white/40 text-xs">{t.nodes} nodes &nbsp;·&nbsp; {t.edges} edges</p>
                      <button data-testid={`dm-use-${t.id}`} onClick={() => setConfirm(t)}
                        className="inline-flex items-center gap-1 px-4 py-1.5 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium">
                        use <ChevronRight size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          /* My Flows */
          <div className="space-y-3" data-testid="dm-flows-list">
            {flows.length === 0 ? (
              <div className="rounded-2xl bg-[#111] border border-white/10 p-10 text-center">
                <Sparkles size={32} className="mx-auto text-orange-400 mb-3" />
                <h3 className="text-xl font-semibold mb-1">No flows yet</h3>
                <p className="text-white/50 text-sm mb-5">Pick a template above and we'll create a draft flow in your AutoPostr workspace.</p>
                <button onClick={() => setTab('templates')} className="px-5 py-2 rounded-full bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold">Browse templates</button>
              </div>
            ) : (
              flows.map(f => (
                <div key={f.flow_id} data-testid={`dm-flow-${f.flow_id}`}
                  className="rounded-xl bg-[#111] border border-white/10 p-4 flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium">{f.name}</p>
                    <p className="text-white/40 text-xs">ID {f.flow_id} · created {f.created_at?.slice(0, 10)}</p>
                  </div>
                  <span className={`text-xs px-2.5 py-1 rounded-full ${
                    f.status === 'ACTIVE' ? 'bg-emerald-500/15 text-emerald-400' :
                    f.status === 'PAUSED' ? 'bg-yellow-500/15 text-yellow-400' :
                    'bg-white/10 text-white/60'}`}>{f.status || 'DRAFT'}</span>
                  <div className="flex gap-2">
                    {f.status !== 'ACTIVE' && (
                      <button data-testid={`dm-activate-${f.flow_id}`} onClick={() => patchFlowStatus(f, 'ACTIVE')}
                        className="px-3 py-1.5 rounded-lg bg-emerald-500/10 text-emerald-400 text-xs hover:bg-emerald-500/20 flex items-center gap-1">
                        <Play size={12} /> Activate
                      </button>
                    )}
                    {f.status === 'ACTIVE' && (
                      <>
                        <button data-testid={`dm-rebind-${f.flow_id}`} onClick={() => patchFlowStatus(f, 'ACTIVE')}
                          className="px-3 py-1.5 rounded-lg bg-sky-500/10 text-sky-400 text-xs hover:bg-sky-500/20 flex items-center gap-1" title="Change the account this flow listens on">
                          Change channel
                        </button>
                        <button data-testid={`dm-pause-${f.flow_id}`} onClick={() => patchFlowStatus(f, 'PAUSED')}
                          className="px-3 py-1.5 rounded-lg bg-yellow-500/10 text-yellow-400 text-xs hover:bg-yellow-500/20 flex items-center gap-1">
                          <Pause size={12} /> Pause
                        </button>
                      </>
                    )}
                    <button data-testid={`dm-edit-${f.flow_id}`} onClick={() => setEditingFlow(f.flow_id)}
                      className="px-3 py-1.5 rounded-lg bg-orange-500/10 text-orange-400 text-xs hover:bg-orange-500/20">Edit</button>
                    <button data-testid={`dm-delete-${f.flow_id}`} onClick={() => deleteFlow(f)}
                      className="px-3 py-1.5 rounded-lg bg-red-500/10 text-red-400 text-xs hover:bg-red-500/20 flex items-center gap-1">
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
      {editingFlow && (
        <FlowEditor flowId={editingFlow} onClose={() => setEditingFlow(null)}
          onSaved={() => { setEditingFlow(null); loadFlows(); setToast({ type: 'success', text: 'Flow saved.' }); }} />
      )}

      {activating && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="dm-activate-modal">
          <div className="bg-[#111] border border-white/10 rounded-2xl max-w-md w-full p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold">{activating.flow.status === 'ACTIVE' ? 'Change channel' : 'Activate flow'}</h3>
                <p className="text-white/50 text-xs mt-0.5 line-clamp-1">{activating.flow.name}</p>
              </div>
              <button onClick={() => setActivating(null)} className="text-white/60 hover:text-white"><X size={18} /></button>
            </div>
            <p className="text-white/60 text-sm mb-4">Which linked AutoPostr accounts should this flow listen on?</p>
            {activating.instagramOnly && (
              <div className="p-3 rounded-lg bg-sky-500/10 border border-sky-500/30 text-sky-300 text-xs mb-3" data-testid="dm-activate-ig-only">
                This template uses Instagram-only features (public comment reply + follow gate), so only your Instagram accounts are available here.
              </div>
            )}
            {(() => {
              const autopostrVisible = activating.instagramOnly
                ? accounts.filter(a => (a.platform || '').toLowerCase() === 'instagram')
                : accounts;
              const chat33Visible = activating.instagramOnly
                ? chat33Channels.filter(c => (c.platform || '').toLowerCase() === 'instagram')
                : chat33Channels;
              // For comment triggers the AutoPostr (publish-only) accounts can never fire,
              // so hide them entirely to keep the choice unambiguous.
              const showAutopostr = !activating.hasCommentTrigger;

              const ConnectInChat33Cta = ({ compact }) => (
                <div className={`flex gap-2 ${compact ? '' : 'flex-wrap'}`}>
                  <button type="button" onClick={() => openConnect('instagram')}
                    data-testid="dm-connect-instagram"
                    className={`inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-pink-500 to-purple-500 text-white font-medium ${compact ? 'px-3 py-1.5 text-xs' : 'px-4 py-2.5 text-sm'} hover:brightness-110`}>
                    Connect Instagram ↗
                  </button>
                  <button type="button" onClick={() => openConnect('facebook')}
                    data-testid="dm-connect-facebook"
                    className={`inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-blue-500 to-sky-500 text-white font-medium ${compact ? 'px-3 py-1.5 text-xs' : 'px-4 py-2.5 text-sm'} hover:brightness-110`}>
                    Connect Facebook ↗
                  </button>
                </div>
              );

              const noneAvailable = (showAutopostr ? autopostrVisible.length : 0) + chat33Visible.length === 0;
              if (noneAvailable) {
                return (
                  <div className="p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/30 mb-4" data-testid="dm-activate-no-accounts">
                    <p className="text-yellow-300 text-sm font-medium mb-1">
                      {activating.hasCommentTrigger ? 'No Instagram channel connected to Chat33 yet' : 'No matching accounts'}
                    </p>
                    <p className="text-yellow-200/80 text-xs mb-3">
                      {activating.hasCommentTrigger
                        ? "Connect your Instagram through AutoPostr — we broker Chat33's OAuth so the account lands in your workspace with inbound comments enabled. We'll pick up the new account automatically."
                        : 'Connect an account to use this flow.'}
                    </p>
                    <ConnectInChat33Cta />
                    <p className="text-yellow-200/50 text-[11px] mt-3 flex items-center gap-1.5">
                      <Loader2 size={11} className="animate-spin" /> Watching for new channels…
                    </p>
                  </div>
                );
              }
              const Row = ({ a, tagColor, tagLabel }) => {
                const platform = (a.platform || '').toLowerCase();
                const platformColor = platform === 'instagram' ? 'bg-pink-500/15 text-pink-300'
                  : platform === 'tiktok' ? 'bg-black text-white border border-white/20'
                  : platform === 'facebook' ? 'bg-blue-500/15 text-blue-300'
                  : platform === 'youtube' ? 'bg-red-500/15 text-red-300'
                  : 'bg-white/10 text-white/60';
                return (
                  <label key={a.id} data-testid={`dm-activate-account-${a.id}`}
                    className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer text-sm transition-colors ${activating.selected.includes(a.id) ? 'border-emerald-500 bg-emerald-500/10' : 'border-white/10 bg-white/5 hover:border-white/20'}`}>
                    <input type="checkbox" checked={activating.selected.includes(a.id)}
                      onChange={() => setActivating(prev => ({ ...prev, selected: prev.selected.includes(a.id) ? prev.selected.filter(x => x !== a.id) : [...prev.selected, a.id] }))}
                      className="w-4 h-4 accent-emerald-500" />
                    <span className={`text-[10px] px-2 py-0.5 rounded-md font-medium uppercase tracking-wider ${platformColor}`}>{platform || 'social'}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm truncate">{a.username || a.display_name || 'Account'}</p>
                    </div>
                    <span className={`text-[9px] px-1.5 py-0.5 rounded font-medium ${tagColor}`}>{tagLabel}</span>
                  </label>
                );
              };
              return (
                <div className="space-y-3 mb-4 max-h-80 overflow-y-auto" data-testid="dm-activate-account-list">
                  {chat33Visible.length > 0 && (
                    <div>
                      <p className="text-emerald-400 text-[11px] font-semibold uppercase tracking-wider mb-1.5">Chat33 dashboard-connected ✓ supports comments</p>
                      <div className="space-y-1.5">
                        {chat33Visible.map(a => <Row key={a.id} a={a} tagColor="bg-emerald-500/20 text-emerald-300" tagLabel="LIVE" />)}
                      </div>
                    </div>
                  )}
                  {showAutopostr && autopostrVisible.length > 0 && (
                    <div>
                      <p className="text-white/40 text-[11px] font-semibold uppercase tracking-wider mb-1.5">AutoPostr-connected (publish-only)</p>
                      <div className="space-y-1.5">
                        {autopostrVisible.map(a => <Row key={a.id} a={a} tagColor="bg-yellow-500/20 text-yellow-300" tagLabel="PUB-ONLY" />)}
                      </div>
                    </div>
                  )}
                  {activating.hasCommentTrigger && (
                    <div className="pt-2 border-t border-white/5 flex items-center justify-between gap-3 flex-wrap">
                      <p className="text-white/40 text-[11px]">Need another IG or FB account?</p>
                      <ConnectInChat33Cta compact />
                    </div>
                  )}
                </div>
              );
            })()}
            {activating.bindingWarning && (
              <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-xs mb-3 space-y-2" data-testid="dm-binding-warning">
                <p className="font-semibold text-red-200">Comment-to-DM blocked by Chat33</p>
                <p className="leading-relaxed">
                  Chat33 only runs comment-to-DM on Instagram accounts connected directly inside chat33.io. Accounts linked through AutoPostr are publish-only — Chat33 doesn't receive inbound comments for them.
                </p>
                <p className="leading-relaxed">To fix this: open Chat33, add your Instagram as a dashboard-connected channel, then come back and re-activate.</p>
                <a href={activating.bindingWarning.connect_url} target="_blank" rel="noopener noreferrer"
                  data-testid="dm-connect-in-chat33"
                  className="inline-block mt-1 px-3 py-1.5 rounded-md bg-white/10 hover:bg-white/15 text-red-200 font-medium">
                  Open chat33.io → Channels ↗
                </a>
              </div>
            )}
            {activating.error && (
              <div className="mb-3 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-xs whitespace-pre-line">
                {activating.error}
              </div>
            )}
            <div className="flex gap-2 justify-end">
              <button onClick={() => setActivating(null)} className="px-4 py-2 rounded-lg bg-white/5 text-white/70 text-sm">Cancel</button>
              <button data-testid="dm-activate-confirm" onClick={confirmActivate} disabled={activating.loading || accounts.length === 0 || activating.selected.length === 0}
                className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed">
                {activating.loading ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
                Activate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
